import { format, parseISO } from "date-fns";
import { documentTypeLabel, occupationLabel } from "@/shared/constants/guestForm";
import type { GuestInfo } from "@/shared/types";

// Label/value row. Renders nothing when the value is empty, so bookings without
// the newer guest fields collapse cleanly and group separators don't leave
// empty gaps.
export const DetailRow = ({
  label,
  value,
  breakAll = false,
}: {
  label: string;
  value?: string;
  breakAll?: boolean;
}) =>
  value ? (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      <dd className={`text-right${breakAll ? " break-all" : ""}`}>{value}</dd>
    </div>
  ) : null;

/** Safe format for the ISO 'yyyy-MM-dd' date of birth stored on a booking. */
const formatDateOfBirth = (value?: string): string | undefined => {
  if (!value) return undefined;
  const parsed = parseISO(value);
  return isNaN(parsed.getTime()) ? value : format(parsed, "dd/MM/yyyy");
};

function GuestDetails({ guest, showContact }: { guest: GuestInfo; showContact: boolean }) {
  return (
    <>
      {/* Contact */}
      <div className="space-y-1">
        {showContact && (
          <>
            <DetailRow label="Name" value={guest.name} />
            <DetailRow label="Email" value={guest.email} breakAll />
            <DetailRow label="Mobile" value={guest.phone} />
          </>
        )}
        <DetailRow label="Emergency Contact" value={guest.emergencyContact} />
      </div>

      {/* Personal */}
      {(guest.dateOfBirth || guest.nationality || guest.occupation) && (
        <div className="space-y-1 border-t pt-3">
          <DetailRow label="Date of Birth" value={formatDateOfBirth(guest.dateOfBirth)} />
          <DetailRow label="Nationality" value={guest.nationality} />
          <DetailRow
            label="Occupation"
            value={guest.occupation ? occupationLabel(guest.occupation) : undefined}
          />
        </div>
      )}

      {/* Identification */}
      {(guest.documentType || guest.documentNumber || guest.taxNumber) && (
        <div className="space-y-1 border-t pt-3">
          <DetailRow
            label="Document type"
            value={guest.documentType ? documentTypeLabel(guest.documentType) : undefined}
          />
          <DetailRow label="ID / Passport Nr" value={guest.documentNumber} breakAll />
          <DetailRow label="Tax Number" value={guest.taxNumber} />
        </div>
      )}

      {/* Address */}
      {(guest.country || guest.address || guest.postalCode || guest.city) && (
        <div className="space-y-1 border-t pt-3">
          <DetailRow label="Country" value={guest.country} />
          <DetailRow label="Address" value={guest.address} />
          <DetailRow label="Postal code" value={guest.postalCode} />
          <DetailRow label="City" value={guest.city} />
        </div>
      )}
    </>
  );
}

interface GuestDetailsBlockProps {
  guests: GuestInfo[];
  /** Omit the "Student(s)" heading when the host dialog already has one. */
  showHeading?: boolean;
  /**
   * Omit name, email and mobile. For the contract dialog, which already lists
   * them from the contract record (where they may legitimately differ).
   */
  showContact?: boolean;
}

/**
 * The full personal-information set collected per guest at booking (DEV2-755),
 * grouped as Contact / Personal / Identification / Address.
 *
 * Shared by the bookings detail modal and the availability-calendar dialogs so
 * the admin sees the same data wherever a booking is opened. Fields with no
 * value are hidden: bookings created before the DEV2-1038 write-path fix only
 * ever stored name, email and phone.
 */
export function GuestDetailsBlock({
  guests,
  showHeading = true,
  showContact = true,
}: GuestDetailsBlockProps) {
  if (guests.length === 0) return null;
  const multiple = guests.length > 1;

  return (
    <div>
      {showHeading && (
        <h4 className="font-medium mb-2">{multiple ? "Students" : "Student"}</h4>
      )}
      <div className="space-y-3">
        {guests.map((guest, i) => (
          <dl
            key={i}
            className={multiple ? "space-y-3 text-sm rounded-lg border p-3" : "space-y-3 text-sm"}
          >
            {multiple && (
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Student {i + 1}
              </p>
            )}
            <GuestDetails guest={guest} showContact={showContact} />
          </dl>
        ))}
      </div>
    </div>
  );
}
